import { Alert, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { getStringConfigValue, subscribeToRemoteConfigUpdates } from 'firebase';

const fallbackTitle = 'Home page temporarily under maintenance';
const fallbackMessage =
  'Thank you for your patience as we complete this update';

type MaintenanceBannerConfig = {
  enabled: boolean;
  title: string;
  message: string;
};

type DashboardBannerProps = {
  configKey: 'dashboard_banner' | 'provider_dashboard_banner';
  icon?: React.ReactNode;
};

const getBannerConfig = (
  configKey: DashboardBannerProps['configKey']
): MaintenanceBannerConfig => {
  try {
    const bannerValue = getStringConfigValue(configKey);
    const parsedConfig = JSON.parse(
      bannerValue
    ) as Partial<MaintenanceBannerConfig>;

    return {
      enabled: parsedConfig.enabled === true,
      title:
        typeof parsedConfig.title === 'string' && parsedConfig.title.trim()
          ? parsedConfig.title.trim()
          : fallbackTitle,
      message:
        typeof parsedConfig.message === 'string' && parsedConfig.message.trim()
          ? parsedConfig.message.trim()
          : fallbackMessage,
    };
  } catch {
    return {
      enabled: false,
      title: fallbackTitle,
      message: fallbackMessage,
    };
  }
};

export const DashboardBanner = ({ configKey, icon }: DashboardBannerProps) => {
  const [bannerConfig, setBannerConfig] = useState(() =>
    getBannerConfig(configKey)
  );

  useEffect(() => {
    setBannerConfig(getBannerConfig(configKey));

    return subscribeToRemoteConfigUpdates(() => {
      setBannerConfig(getBannerConfig(configKey));
    });
  }, [configKey]);

  if (!bannerConfig.enabled) {
    return null;
  }

  return (
    <Alert
      color="yellow"
      icon={icon ?? <IconAlertTriangle size={18} />}
      mb="lg"
      radius="md"
      title={bannerConfig.title}
      variant="light"
      data-testid="home-maintenance-banner"
    >
      <Stack gap={4}>
        <Text size="sm">{bannerConfig.message}</Text>
      </Stack>
    </Alert>
  );
};
